/* eslint-disable global-require */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faEnvelope } from "@fortawesome/free-regular-svg-icons";
import {
  faFacebookMessenger,
  faFacebookSquare,
  faLinkedin,
  faTwitter,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";
import { Col, message, Row, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EmailShareButton,
  FacebookMessengerShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from "react-share";
import { useQuery } from "@apollo/client";
import { GET_INVITATION_CODE } from "graphql/queries/GetProfile";
import {
  BigShareButton,
  Copy,
  CopyButton,
  CopyText,
  SmallShareButton,
  StyledDiv,
  StyledH4,
  StyledImage,
  StyledParagraph,
  StyledSection,
} from "./RefererStyle";

const Referer = () => {
  const { t } = useTranslation("refer_a_friend");
  const { data, error, loading } = useQuery(GET_INVITATION_CODE);
  // STATE MANAGEMENT
  const [referralCode, setReferralCode] = useState("123456");
  const [copied, setCopied] = useState(false);
  const [username, setUsername] = useState("");
  const [locale, setLocale] = useState("");

  // TYPE CASTING for fontawesomex`x`
  const faEnvelopeIcon = faEnvelope as IconProp;
  const faFacebookMessengerIcon = faFacebookMessenger as IconProp;
  const faWhatsappIcon = faWhatsapp as IconProp;
  const faFacebookSquareIcon = faFacebookSquare as IconProp;
  const faTwitterIcon = faTwitter as IconProp;
  const faLinkedinIcon = faLinkedin as IconProp;

  const antIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "blue" }} spin />
  );

  const getReferralLink = () =>
    `${window.location.protocol}//${window.location.hostname}${
      window.location.port && ":".concat(window.location.port)
    }/referral/${encodeURIComponent(referralCode)}?referer=${encodeURIComponent(
      username
    )}&lang=${locale}`;

  const getMessage = () =>
    t(
      "Hello,\n\nI am studying online with LTL Flexi Classes at the moment. If you use my refer a friend link "
    )
      .concat(getReferralLink())
      .concat(
        t(
          " and sign up you will get 5 classes for free with the discount code "
        )
      )
      .concat(referralCode)
      .concat(
        t(
          ".\n\nWe can take some online group classes together too.\n\nRegards,\n\n"
        )
      )
      .concat(username)
      .concat("\n\n");

  const getShortMessage = () =>
    t(
      "Hello,\n\nI am studying online with LTL Flexi Classes at the moment. If you use the link below"
    )
      .concat(
        t(
          " and sign up you will get 5 classes for free with the discount code "
        )
      )
      .concat(referralCode)
      .concat(".")
      .concat("\n\n");

  useEffect(() => {
    if (data?.me?.givenName) {
      setUsername(data.me.givenName);
    }
  }, [data]);

  useEffect(() => {
    if (error)
      message.error(
        error.message || "Something went wrong. Please try again!",
        2
      );
  }, [error]);

  useEffect(() => {
    if (data?.me?.outgoingInvitationCode && data?.me?.locale) {
      setReferralCode(data.me.outgoingInvitationCode);
      setLocale(data.me.locale);
    }
  }, [data]);

  const handleWhatsAppDialog = () => {
    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(getMessage())}`,
      "_blank",
      "popup"
    );
  };
  if (loading)
    return (
      <div style={{ width: "100%" }}>
        <Spin style={{ marginLeft: "50%" }} indicator={antIcon} />
      </div>
    );
  return (
    <>
      <StyledSection>
        <StyledParagraph>
          {t(
            "Invite a friend to Flexi Classes and get 5 free group classes for both of you. Your friend will get 5 free classes to start their Flexi program. Once they purchase a subscription, you will get 5 free group classes."
          )}
        </StyledParagraph>
      </StyledSection>
      <StyledSection>
        <StyledH4>{t("Share your invitation with a friend")}</StyledH4>
        <StyledParagraph>
          {t(
            "This is the link to give to your friends for their free 5 group classes. You can copy it or send it directly via Email, Messenger or WhatsApp."
          )}
        </StyledParagraph>
        <Row justify="space-between" gutter={20}>
          <Col xs={24} sm={24} md={24} lg={14}>
            <Copy>
              <CopyText>{getReferralLink()}</CopyText>
              <CopyButton
                onClick={() => {
                  navigator.clipboard.writeText(getReferralLink());
                  setCopied(true);
                }}
                disabled={copied}
              >
                {copied ? t("Copied!") : t("Copy")}
              </CopyButton>
            </Copy>
          </Col>
          <Col xs={24} sm={24} md={24} lg={10}>
            <EmailShareButton
              url={getReferralLink()}
              subject="LTL Flexi Classes"
              body={getMessage()}
            >
              <SmallShareButton style={{ backgroundColor: "#148de5" }}>
                <FontAwesomeIcon icon={faEnvelopeIcon} size="2x" />
              </SmallShareButton>
            </EmailShareButton>
            <FacebookMessengerShareButton
              url={getReferralLink()}
              appId="2115904375230871"
            >
              <SmallShareButton style={{ backgroundColor: "#1070b6" }}>
                <FontAwesomeIcon icon={faFacebookMessengerIcon} size="2x" />
              </SmallShareButton>
            </FacebookMessengerShareButton>
            <button
              style={{
                display: "inline",
                backgroundColor: "transparent",
                border: "none",
                padding: 0,
              }}
              type="button"
              onClick={handleWhatsAppDialog}
            >
              <SmallShareButton style={{ backgroundColor: "#25d366" }}>
                <FontAwesomeIcon icon={faWhatsappIcon} size="2x" />
              </SmallShareButton>
            </button>
          </Col>
        </Row>
      </StyledSection>
      <StyledSection>
        <StyledH4>{t("Invite friends on social media")}</StyledH4>
        <StyledParagraph>
          {t(
            "Create a post on your social media and share your code with anyone following you."
          )}
        </StyledParagraph>
        <Row>
          <FacebookShareButton
            url={getReferralLink()}
            quote={getMessage()}
            hashtag="freecredits"
          >
            <BigShareButton style={{ backgroundColor: "#1070b6" }}>
              <FontAwesomeIcon
                icon={faFacebookSquareIcon}
                size="2x"
                style={{ marginRight: "10px" }}
              />
              {t("Share on Facebook")}
            </BigShareButton>
          </FacebookShareButton>
          <TwitterShareButton
            url={getReferralLink()}
            title={getShortMessage()}
            hashtags={["freecredits", "flexiclasses"]}
          >
            <BigShareButton style={{ backgroundColor: "#148de5" }}>
              <FontAwesomeIcon
                icon={faTwitterIcon}
                size="2x"
                style={{ marginRight: "10px" }}
              />
              {t("Share on Twitter")}
            </BigShareButton>
          </TwitterShareButton>
          <LinkedinShareButton
            url={getReferralLink()}
            title="LTL Flexi Classes"
            summary={getMessage()}
            source={getReferralLink()}
          >
            <BigShareButton style={{ backgroundColor: "#1070b6" }}>
              <FontAwesomeIcon
                icon={faLinkedinIcon}
                size="2x"
                style={{ marginRight: "10px" }}
              />
              {t("Share on Linkedin")}
            </BigShareButton>
          </LinkedinShareButton>
        </Row>
      </StyledSection>

      <StyledDiv>
        <Row justify="space-between">
          <Col xs={24} sm={24} md={24} lg={11} xl={8}>
            <StyledH4>
              {t("For your friend")} <span>{t("5 Free Group Classes")}</span>
            </StyledH4>
            <StyledParagraph>
              {t(
                "When your friend signs up they will receive credits for 5 free group classes. Credits can be converted into 1-on-1 classes. Works both with the free trial and buying a subscription."
              )}
            </StyledParagraph>
          </Col>
          <Col xs={24} sm={24} md={24} lg={11} xl={8}>
            <StyledH4>
              {t("For you")} <span>{t("5 Free Group Classes")}</span>
            </StyledH4>
            <StyledParagraph>
              {t(
                "Once your friend purchases any kind of subscription you will receive credits for 5 free group classes. All credits can be used to book 1-on-1 classes too."
              )}
            </StyledParagraph>
          </Col>
        </Row>
        <Row justify="center">
          <Col xs={24} sm={24} md={24} lg={15} xl={13}>
            <StyledImage
              src={require("assets/tiger-background.png")}
              alt="tiger background"
            />
          </Col>
        </Row>
      </StyledDiv>
    </>
  );
};

export default Referer;
